import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
	makeStyles,
	Dialog,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	ButtonGroup,
	CircularProgress,
	Grid,
	Tooltip,
	IconButton,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Paper,
	Chip,
} from "@material-ui/core";
import {
	TrendingUp,
	Close as CloseIcon,
	HelpOutline,
	Equalizer,
	Launch,
	LocalOffer,
} from "@material-ui/icons";
import api from "../../services/api";
import { toast } from "react-toastify";
import FileCopyIcon from "@material-ui/icons/FileCopy";

const useStyles = makeStyles(theme => ({
	dialogPaper: {
		borderRadius: 12,
		padding: theme.spacing(1),
		maxWidth: 1080,
		width: "100%",
	},
	statGrid: {
		display: "grid",
		gridTemplateColumns: "1.1fr 1.35fr 0.95fr 1.1fr 0.95fr 1fr",
		gap: theme.spacing(1),
		width: "100%",
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "repeat(3, 1fr)",
		},
	},
	header: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: theme.spacing(1, 2),
	},
	titleWrapper: {
		display: "flex",
		alignItems: "center",
		gap: 8,
	},
	title: {
		fontWeight: 800,
		fontSize: "1.15rem",
		color: "#0f172a",
	},
	badge: {
		display: "inline-block",
		fontSize: "0.75rem",
		fontWeight: 700,
		padding: "2px 8px",
		borderRadius: 12,
		color: "#ffffff",
	},
	statCard: {
		padding: theme.spacing(1.2),
		backgroundColor: "#f8fafc",
		border: "1px solid #e2e8f0",
		borderRadius: 8,
		textAlign: "center",
		height: "100%",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
	},
	statValue: {
		fontSize: "1.15rem",
		fontWeight: 800,
		color: "#1e293b",
	},
	statLabel: {
		fontSize: "0.72rem",
		fontWeight: 600,
		color: "#64748b",
		textTransform: "uppercase",
		marginTop: 4,
	},
	tokenBarContainer: {
		marginTop: theme.spacing(1.5),
		marginBottom: theme.spacing(2),
		backgroundColor: "#f1f5f9",
		padding: theme.spacing(1.2),
		borderRadius: 8,
	},
	barWrapper: {
		height: 8,
		width: "100%",
		backgroundColor: "#e2e8f0",
		borderRadius: 4,
		overflow: "hidden",
		display: "flex",
	},
	promptBar: {
		backgroundColor: "#3b82f6",
		height: "100%",
	},
	candidateBar: {
		backgroundColor: "#10b981",
		height: "100%",
	},
	legend: {
		display: "flex",
		justifyContent: "space-between",
		fontSize: "0.78rem",
		color: "#334155",
		marginTop: 6,
	},
	dotPrompt: {
		display: "inline-block",
		width: 8,
		height: 8,
		borderRadius: "50%",
		backgroundColor: "#3b82f6",
		marginRight: 6,
	},
	dotCandidate: {
		display: "inline-block",
		width: 8,
		height: 8,
		borderRadius: "50%",
		backgroundColor: "#10b981",
		marginRight: 6,
	},
	historyTitle: {
		fontWeight: 700,
		fontSize: "0.92rem",
		color: "#1e293b",
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1),
		display: "flex",
		alignItems: "center",
		gap: 6,
	},
	tablePaper: {
		border: "1px solid #e2e8f0",
		borderRadius: 8,
		maxHeight: 200,
		overflowY: "auto",
	},
	ticketLink: {
		display: "inline-flex",
		alignItems: "center",
		gap: 4,
		fontWeight: 700,
		color: "#2563eb",
		textDecoration: "none",
		cursor: "pointer",
		"&:hover": {
			textDecoration: "underline",
		},
	},
}));

const AiUsageModal = ({ open, onClose, whatsApp }) => {
	const classes = useStyles();
	const history = useHistory();
	const [period, setPeriod] = useState("last30Days"); // today | last7Days | last30Days
	const [loading, setLoading] = useState(true);
	const [report, setReport] = useState(null);
	const [errorDetails, setErrorDetails] = useState(null);

	const loadData = async () => {
		if (!whatsApp?.id) {
			setLoading(false);
			return;
		}
		setLoading(true);
		setErrorDetails(null);
		try {
			const targetUrl = `/whatsapp/ai-usage/${whatsApp.id}`;
			const { data } = await api.get(targetUrl);
			setReport(data);
		} catch (err) {
			console.error("[AiUsageModal Error]:", err);
			const errInfo = {
				message: err.message || "Erro desconhecido",
				status: err.response?.status || "Sem Status (Network/CORS/Auth)",
				statusText: err.response?.statusText || "",
				data: err.response?.data || null,
				url: err.config?.url || `/whatsapp/ai-usage/${whatsApp?.id}`,
				baseURL: err.config?.baseURL || "",
				whatsappId: whatsApp?.id,
				whatsappName: whatsApp?.name
			};
			setErrorDetails(errInfo);
			toast.error(`Erro ao carregar dados da IA: ${err.message || ""}`);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open && whatsApp?.id) {
			loadData();
		}
	}, [open, whatsApp?.id]);

	if (!whatsApp) return null;

	const currentMetrics = report ? report[period] : null;

	const promptTokens = currentMetrics?.promptTokens || 0;
	const candidatesTokens = currentMetrics?.candidatesTokens || 0;
	const totalTokens = currentMetrics?.totalTokens || (promptTokens + candidatesTokens);
	const uniquePatients = currentMetrics?.uniquePatients || 0;
	const callsCount = currentMetrics?.callsCount || 0;
	const followUpCallsCount = currentMetrics?.followUpCallsCount || 0;
	const autoCategorizedCount = currentMetrics?.autoCategorizedCount || 0;
	const costUsd = currentMetrics?.estimatedCostUsd || 0;
	const costBrl = costUsd * 5.6; // Cotação estimada BRL

	const promptPct = totalTokens > 0 ? (promptTokens / totalTokens) * 100 : 0;
	const candidatePct = totalTokens > 0 ? (candidatesTokens / totalTokens) * 100 : 0;

	const formatNumber = num => {
		if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
		if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
		return (num || 0).toLocaleString();
	};

	const formatBrl = val => {
		if (!val || val === 0) return "R$ 0,00";
		if (val < 0.01) return `≈ R$ ${val.toFixed(3).replace(".", ",")}`;
		return `≈ R$ ${val.toFixed(2).replace(".", ",")}`;
	};

	const formatDate = dateStr => {
		if (!dateStr) return "";
		const parts = dateStr.split("-");
		if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
		return dateStr;
	};

	const formatDateTime = dateStr => {
		if (!dateStr) return "-";
		const d = new Date(dateStr);
		return d.toLocaleDateString("pt-BR", {
			day: "2-digit",
			month: "2-digit",
			hour: "2-digit",
			minute: "2-digit"
		});
	};

	const handleOpenTicket = ticketId => {
		if (!ticketId) return;
		onClose();
		history.push(`/tickets/${ticketId}`);
	};

	const handleCopyReport = () => {
		const pLabel = period === "today" ? "Hoje" : period === "last7Days" ? "Últimos 7 Dias" : "Últimos 30 Dias";
		let text = `📊 **Relatório de Consumo de IA — ${whatsApp.name}**\n`;
		text += `📅 **Período:** ${pLabel}\n`;
		text += `⚡ **Total de Tokens:** ${formatNumber(totalTokens)} (Entrada: ${formatNumber(promptTokens)} / Saída: ${formatNumber(candidatesTokens)})\n`;
		text += `💵 **Custo Estimado:** $${Number(costUsd).toFixed(4)} (${formatBrl(costBrl)})\n`;
		text += `👥 **Pacientes Atendidos:** ${uniquePatients} pacientes\n`;
		text += `💬 **Chamadas:** Respostas Chat: ${callsCount} | Follow-Ups: ${followUpCallsCount} | Categorizados: ${autoCategorizedCount}\n\n`;

		if (report?.recentCallLogs && report.recentCallLogs.length > 0) {
			text += `🔍 **Últimos Disparos por Ticket:**\n`;
			report.recentCallLogs.slice(0, 15).forEach(l => {
				text += `- Ticket #${l.ticketId || "?"} (${l.contactName || "Cliente"}): ${l.callType} | ${formatNumber(l.totalTokens)} tokens | $${Number(l.estimatedCostUsd || 0).toFixed(4)}\n`;
			});
		} else {
			text += `*Nenhum disparo detalhado recente registrado para esta conexão.*\n`;
		}

		try {
			if (navigator?.clipboard?.writeText) {
				navigator.clipboard.writeText(text).then(() => {
					toast.success("📋 Relatório copiado com sucesso! Pode colar aqui no chat.");
				}).catch(() => {
					fallbackCopy(text);
				});
			} else {
				fallbackCopy(text);
			}
		} catch (copyErr) {
			fallbackCopy(text);
		}
	};

	const fallbackCopy = text => {
		try {
			const textArea = document.createElement("textarea");
			textArea.value = text;
			textArea.style.position = "fixed";
			textArea.style.left = "-999999px";
			textArea.style.top = "-999999px";
			document.body.appendChild(textArea);
			textArea.focus();
			textArea.select();
			const successful = document.execCommand("copy");
			textArea.remove();
			if (successful) {
				toast.success("📋 Relatório copiado com sucesso! Pode colar aqui no chat.");
			} else {
				toast.error("Não foi possível copiar automaticamente. Selecione o texto.");
			}
		} catch (err) {
			console.error("Fallback copy error:", err);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			classes={{ paper: classes.dialogPaper }}
		>
			<div className={classes.header}>
				<div className={classes.titleWrapper}>
					<Equalizer style={{ color: "#2563eb", fontSize: 28 }} />
					<div>
						<Typography className={classes.title}>
							Consumo de IA — {whatsApp.name}
						</Typography>
						<Typography variant="caption" style={{ color: "#64748b" }}>
							Métricas e custos de processamento com Google Gemini (Flash-Lite)
						</Typography>
					</div>
					{whatsApp.color && (
						<span
							className={classes.badge}
							style={{ backgroundColor: whatsApp.color }}
						>
							{whatsApp.name}
						</span>
					)}
				</div>

				<IconButton size="small" onClick={onClose}>
					<CloseIcon />
				</IconButton>
			</div>

			<DialogContent dividers>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
					<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
						<TrendingUp fontSize="small" style={{ color: "#3b82f6" }} />
						<Typography style={{ fontWeight: 700, fontSize: "0.95rem" }}>
							Resumo do Período
						</Typography>
						<Tooltip title="Dados extraídos em tempo real do usageMetadata oficial retornado pelo Gemini. Base Gemini 2.0 Flash-Lite ($0.075 input / $0.30 output por 1M tokens).">
							<HelpOutline style={{ fontSize: 16, color: "#94a3b8", cursor: "pointer" }} />
						</Tooltip>
					</div>

					<ButtonGroup size="small" variant="outlined">
						<Button
							color={period === "today" ? "primary" : "default"}
							variant={period === "today" ? "contained" : "outlined"}
							onClick={() => setPeriod("today")}
						>
							Hoje
						</Button>
						<Button
							color={period === "last7Days" ? "primary" : "default"}
							variant={period === "last7Days" ? "contained" : "outlined"}
							onClick={() => setPeriod("last7Days")}
						>
							7 Dias
						</Button>
						<Button
							color={period === "last30Days" ? "primary" : "default"}
							variant={period === "last30Days" ? "contained" : "outlined"}
							onClick={() => setPeriod("last30Days")}
						>
							30 Dias
						</Button>
					</ButtonGroup>
				</div>

				{loading ? (
					<div style={{ textAlign: "center", padding: "48px 16px" }}>
						<CircularProgress size={36} color="primary" />
						<Typography variant="body2" style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>
							Carregando métricas de IA em tempo real...
						</Typography>
					</div>
				) : errorDetails ? (
					<div style={{ padding: 16, backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, marginTop: 8 }}>
						<Typography variant="subtitle1" style={{ color: "#991b1b", fontWeight: 800, marginBottom: 8 }}>
							⚠️ Falha ao Obter Métricas da IA
						</Typography>
						<Typography variant="body2" style={{ color: "#7f1d1d", marginBottom: 12 }}>
							O sistema não conseguiu carregar as estatísticas da conexão <strong>{errorDetails.whatsappName} (ID: #{errorDetails.whatsappId})</strong>.
						</Typography>

						<Paper style={{ padding: 12, backgroundColor: "#1e293b", color: "#f8fafc", fontFamily: "monospace", fontSize: "0.8rem", borderRadius: 6, overflowX: "auto", marginBottom: 16 }}>
							<div><strong>Mensagem:</strong> {errorDetails.message}</div>
							<div><strong>HTTP Status:</strong> {String(errorDetails.status)} {errorDetails.statusText}</div>
							<div><strong>Endpoint:</strong> {errorDetails.baseURL}{errorDetails.url}</div>
							{errorDetails.data && (
								<div><strong>Resposta Servidor:</strong> {JSON.stringify(errorDetails.data)}</div>
							)}
						</Paper>

						<div style={{ display: "flex", gap: 8 }}>
							<Button
								variant="contained"
								color="secondary"
								size="small"
								onClick={() => {
									navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
									toast.success("Diagnóstico de erro copiado! Cole no chat.");
								}}
							>
								📋 Copiar Diagnóstico de Erro
							</Button>
							<Button
								variant="outlined"
								color="default"
								size="small"
								onClick={loadData}
							>
								🔄 Tentar Novamente
							</Button>
						</div>
					</div>
				) : !report ? (
					<div style={{ textAlign: "center", padding: "32px 16px", color: "#64748b" }}>
						Nenhum dado disponível.
					</div>
				) : (
					<>
						<div className={classes.statGrid}>
							<div className={classes.statCard}>
								<div className={classes.statValue}>{formatNumber(totalTokens)}</div>
								<div className={classes.statLabel}>Total Tokens</div>
							</div>

							<div className={classes.statCard}>
								<div className={classes.statValue} style={{ color: "#16a34a" }}>
									${Number(costUsd).toFixed(4)}
								</div>
								<div className={classes.statLabel}>
									Custo ({formatBrl(costBrl)})
								</div>
							</div>

							<div className={classes.statCard}>
								<div className={classes.statValue} style={{ color: "#059669" }}>
									{uniquePatients}
								</div>
								<div className={classes.statLabel}>Pacientes</div>
							</div>

							<div className={classes.statCard}>
								<div className={classes.statValue} style={{ color: "#0284c7" }}>
									{callsCount}
								</div>
								<div className={classes.statLabel}>Respostas Chat</div>
							</div>

							<div className={classes.statCard}>
								<div className={classes.statValue} style={{ color: "#d97706" }}>
									{followUpCallsCount}
								</div>
								<div className={classes.statLabel}>Follow-Ups</div>
							</div>

							<div className={classes.statCard}>
								<div className={classes.statValue} style={{ color: "#9333ea" }}>
									{autoCategorizedCount}
								</div>
								<div className={classes.statLabel}>Categorizados</div>
							</div>
						</div>

						<div className={classes.tokenBarContainer}>
							<div className={classes.barWrapper}>
								<div className={classes.promptBar} style={{ width: `${promptPct}%` }} />
								<div className={classes.candidateBar} style={{ width: `${candidatePct}%` }} />
							</div>

							<div className={classes.legend}>
								<span>
									<span className={classes.dotPrompt} />
									Entrada (Prompt + Histórico): <strong>{formatNumber(promptTokens)}</strong> ({promptPct.toFixed(0)}%)
								</span>
								<span>
									<span className={classes.dotCandidate} />
									Saída (Respostas Geradas): <strong>{formatNumber(candidatesTokens)}</strong> ({candidatePct.toFixed(0)}%)
								</span>
							</div>
						</div>

						{/* 1. HISTÓRICO DIÁRIO (ÚLTIMOS 14 DIAS) */}
						<Typography className={classes.historyTitle}>
							📅 Histórico Diário (Últimos 14 dias)
						</Typography>

						<Paper className={classes.tablePaper} variant="outlined">
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell>Data</TableCell>
										<TableCell align="right">Tokens Entrada</TableCell>
										<TableCell align="right">Tokens Saída</TableCell>
										<TableCell align="right">Total Tokens</TableCell>
										<TableCell align="right">Chamadas</TableCell>
										<TableCell align="right">Categorizados</TableCell>
										<TableCell align="right">Custo Est. ($)</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{report?.dailyHistory?.length > 0 ? (
										report.dailyHistory.map(row => (
											<TableRow key={row.id || row.date}>
												<TableCell component="th" scope="row">
													<strong>{formatDate(row.date)}</strong>
												</TableCell>
												<TableCell align="right">{formatNumber(row.promptTokens)}</TableCell>
												<TableCell align="right">{formatNumber(row.candidatesTokens)}</TableCell>
												<TableCell align="right">
													<strong>{formatNumber(row.totalTokens)}</strong>
												</TableCell>
												<TableCell align="right">
													{row.callsCount + (row.followUpCallsCount || 0)}
												</TableCell>
												<TableCell align="right" style={{ color: "#9333ea", fontWeight: 600 }}>
													{row.autoCategorizedCount || 0}
												</TableCell>
												<TableCell align="right" style={{ color: "#16a34a", fontWeight: 600 }}>
													${Number(row.estimatedCostUsd || 0).toFixed(4)}
												</TableCell>
											</TableRow>
										))
									) : (
										<TableRow>
											<TableCell colSpan={7} align="center" style={{ color: "#94a3b8", padding: 16 }}>
												Nenhum histórico diário gravado ainda. As métricas aparecerão aqui automaticamente.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</Paper>

						{/* 2. TABELA DE ÚLTIMOS DISPAROS DE IA POR TICKET */}
						<Typography className={classes.historyTitle} style={{ marginTop: 18 }}>
							<LocalOffer fontSize="small" style={{ color: "#2563eb" }} />
							<span>🔍 Últimos Disparos de IA por Ticket (Auditoria Recente)</span>
						</Typography>

						<Paper className={classes.tablePaper} variant="outlined" style={{ maxHeight: 240 }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell>Data/Hora</TableCell>
										<TableCell>Ticket</TableCell>
										<TableCell>Contato</TableCell>
										<TableCell>Tipo</TableCell>
										<TableCell>Categoria Auto</TableCell>
										<TableCell align="right">Tokens (In / Out)</TableCell>
										<TableCell align="right">Custo ($)</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{report?.recentCallLogs?.length > 0 ? (
										report.recentCallLogs.map(log => {
											const typeLabels = {
												chat: { text: "💬 Chat", bg: "#e0f2fe", color: "#0369a1" },
												followup: { text: "🔄 Follow-Up", bg: "#fef3c7", color: "#b45309" },
												autocategory: { text: "🏷️ Auto-Cat", bg: "#f3e8ff", color: "#7e22ce" },
											};
											const badge = typeLabels[log.callType] || { text: log.callType, bg: "#f1f5f9", color: "#475569" };

											return (
												<TableRow key={log.id}>
													<TableCell>{formatDateTime(log.createdAt)}</TableCell>
													<TableCell>
														{log.ticketId ? (
															<span
																className={classes.ticketLink}
																onClick={() => handleOpenTicket(log.ticketId)}
																title="Abrir este chamado no chat"
															>
																#{log.ticketId} <Launch style={{ fontSize: 13 }} />
															</span>
														) : (
															"-"
														)}
													</TableCell>
													<TableCell>
														<strong>{log.contactName || "Cliente"}</strong>
													</TableCell>
													<TableCell>
														<span
															style={{
																fontSize: "0.7rem",
																fontWeight: 700,
																backgroundColor: badge.bg,
																color: badge.color,
																padding: "2px 6px",
																borderRadius: 6,
															}}
														>
															{badge.text}
														</span>
													</TableCell>
													<TableCell>
														{log.categoryApplied ? (
															<span
																style={{
																	fontSize: "0.72rem",
																	fontWeight: 600,
																	backgroundColor: "#f0fdf4",
																	color: "#15803d",
																	border: "1px solid #bbf7d0",
																	padding: "2px 6px",
																	borderRadius: 6,
																}}
															>
																{log.categoryApplied}
															</span>
														) : (
															<span style={{ color: "#cbd5e1" }}>—</span>
														)}
													</TableCell>
													<TableCell align="right">
														<span style={{ fontSize: "0.75rem" }}>
															{formatNumber(log.promptTokens)} / {formatNumber(log.candidatesTokens)} (<strong>{formatNumber(log.totalTokens)}</strong>)
														</span>
													</TableCell>
													<TableCell align="right" style={{ color: "#16a34a", fontWeight: 600 }}>
														${Number(log.estimatedCostUsd || 0).toFixed(4)}
													</TableCell>
												</TableRow>
											);
										})
									) : (
										<TableRow>
											<TableCell colSpan={7} align="center" style={{ color: "#94a3b8", padding: 16 }}>
												Nenhum disparo detalhado recente registrado ainda.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</Paper>
					</>
				)}
			</DialogContent>

			<DialogActions style={{ justifyContent: "space-between", padding: "8px 16px" }}>
				<Button
					onClick={handleCopyReport}
					color="primary"
					variant="contained"
					size="small"
					startIcon={<FileCopyIcon />}
					style={{ textTransform: "none", fontWeight: 700 }}
				>
					📋 Copiar Relatório para Diagnóstico (IA)
				</Button>
				<Button onClick={onClose} color="default" variant="outlined" size="small">
					Fechar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AiUsageModal;
